import { firestore } from './firebaseConfig';
import { ContratoConfig, CONTRATO_PADRAO, validarContratoConfig } from '../utils/contrato';

// Configurações da plataforma editáveis pelo administrador (painel "Chave PIX e Cadastro"):
//   config/pix      - chave PIX usada em TODOS os pagamentos (matrícula, mensalidade, evento, pacote)
//   config/cadastro - se o cadastro público exige reCAPTCHA
// Leitura pública (a chave já aparece em cada QR Code) e escrita só admin/adm (firestore.rules, match /config).
// Não usa função serverless: tudo direto no Firestore, com as regras protegendo a escrita.

export interface ConfigPix {
  chave: string;
  nome: string; // recebedor (até 25 caracteres, limite do padrão PIX)
  cidade: string; // até 15 caracteres
  atualizadoEm?: number;
  atualizadoPor?: string;
}

export interface ConfigCadastro {
  recaptchaAtivo: boolean;
  // Venda de pacotes de redação (link "Crie uma conta sem matrícula" no login, cadastro avulso e a aba
  // Pacotes do aluno de turma). Começa DESLIGADA e o admin libera em /admin/configuracoes.
  pacotesAtivo: boolean;
  atualizadoEm?: number;
  atualizadoPor?: string;
}

// Valores que estavam fixos no código: seguem valendo enquanto o admin não cadastrar outra chave.
export const CONFIG_PIX_PADRAO: ConfigPix = {
  chave: 'eliasancelmoo19@gmail.com',
  nome: 'Elias Ancelmo',
  cidade: 'Limoeiro',
};

// reCAPTCHA do cadastro público começa DESLIGADO (decisão do cliente para os testes em produção) e o
// admin liga no painel quando quiser.
export const CONFIG_CADASTRO_PADRAO: ConfigCadastro = { recaptchaAtivo: false, pacotesAtivo: false };

export const validarConfigPix = (c: Pick<ConfigPix, 'chave' | 'nome' | 'cidade'>): string | null => {
  const chave = c.chave.trim();
  if (!chave) return 'Informe a chave PIX.';
  if (chave.length > 77) return 'A chave PIX é longa demais (máximo 77 caracteres).';
  if (/\s/.test(chave)) return 'A chave PIX não pode ter espaços.';
  const nome = c.nome.trim();
  if (!nome) return 'Informe o nome do recebedor.';
  if (nome.length > 25) return 'O nome do recebedor pode ter no máximo 25 caracteres.';
  const cidade = c.cidade.trim();
  if (!cidade) return 'Informe a cidade.';
  if (cidade.length > 15) return 'A cidade pode ter no máximo 15 caracteres.';
  return null;
};

const TTL_MS = 60 * 1000;
let cachePix: { valor: ConfigPix; em: number } | null = null;

export const invalidarConfigPix = () => {
  cachePix = null;
};

export const getConfigPix = async (): Promise<ConfigPix> => {
  if (cachePix && Date.now() - cachePix.em < TTL_MS) return cachePix.valor;
  let valor = CONFIG_PIX_PADRAO;
  try {
    const snap = await firestore.collection('config').doc('pix').get();
    const d = snap.exists ? (snap.data() as Partial<ConfigPix>) : null;
    if (d && d.chave && !validarConfigPix({ chave: d.chave, nome: d.nome || '', cidade: d.cidade || '' })) {
      valor = { chave: d.chave, nome: d.nome as string, cidade: d.cidade as string, atualizadoEm: d.atualizadoEm, atualizadoPor: d.atualizadoPor };
    }
  } catch (error) {
    console.error('Erro ao buscar a chave PIX, usando a padrão:', error);
  }
  cachePix = { valor, em: Date.now() };
  return valor;
};

export const salvarConfigPix = async (c: Pick<ConfigPix, 'chave' | 'nome' | 'cidade'>, uid: string) => {
  const erro = validarConfigPix(c);
  if (erro) throw new Error(erro);
  await firestore.collection('config').doc('pix').set(
    { chave: c.chave.trim(), nome: c.nome.trim(), cidade: c.cidade.trim(), atualizadoEm: Date.now(), atualizadoPor: uid },
    { merge: true }
  );
  invalidarConfigPix();
};

export const getConfigCadastro = async (): Promise<ConfigCadastro> => {
  try {
    const snap = await firestore.collection('config').doc('cadastro').get();
    const d = snap.exists ? (snap.data() as Partial<ConfigCadastro>) : null;
    return { ...CONFIG_CADASTRO_PADRAO, ...(d || {}), recaptchaAtivo: d?.recaptchaAtivo === true, pacotesAtivo: d?.pacotesAtivo === true };
  } catch (error) {
    console.error('Erro ao buscar a configuração de cadastro, usando a padrão:', error);
    return CONFIG_CADASTRO_PADRAO;
  }
};

export const salvarConfigCadastro = async (recaptchaAtivo: boolean, uid: string) => {
  await firestore.collection('config').doc('cadastro').set({ recaptchaAtivo, atualizadoEm: Date.now(), atualizadoPor: uid }, { merge: true });
};

export const salvarPacotesAtivo = async (pacotesAtivo: boolean, uid: string) => {
  await firestore.collection('config').doc('cadastro').set({ pacotesAtivo, pacotesAtualizadoEm: Date.now(), pacotesAtualizadoPor: uid }, { merge: true });
};

// ---------- config/contrato: modelo do contrato de prestação de serviços (ver utils/contrato.ts) ----------
// Leitura pública (o cadastro mostra o contrato antes do login). Escrita por admin, secretaria e professor
// (firestore.rules, match /config/contrato). Sem documento, vale CONTRATO_PADRAO (versão 1).
export const getConfigContrato = async (): Promise<ContratoConfig> => {
  try {
    const snap = await firestore.collection('config').doc('contrato').get();
    const d = snap.exists ? (snap.data() as Partial<ContratoConfig>) : null;
    if (!d) return CONTRATO_PADRAO;
    return {
      ...CONTRATO_PADRAO,
      ...d,
      versao: d.versao ?? CONTRATO_PADRAO.versao,
      versaoExigida: d.versaoExigida ?? CONTRATO_PADRAO.versaoExigida,
      contratada: { ...CONTRATO_PADRAO.contratada, ...(d.contratada || {}) },
      textos: { ...CONTRATO_PADRAO.textos, ...(d.textos || {}) },
    };
  } catch (error) {
    console.error('Erro ao buscar o modelo do contrato, usando o padrão:', error);
    return CONTRATO_PADRAO;
  }
};

// Publica o modelo: o texto novo SUBSTITUI o anterior (sem histórico) e a versão sobe 1. Com
// exigirNovoAceite, alunos que aceitaram versões anteriores veem o modal para aceitar de novo.
export const publicarConfigContrato = async (
  novo: Pick<ContratoConfig, 'contratada' | 'foro' | 'textos'>,
  exigirNovoAceite: boolean,
  uid: string
): Promise<ContratoConfig> => {
  const erro = validarContratoConfig(novo);
  if (erro) throw new Error(erro);
  const atual = await getConfigContrato();
  const versao = atual.versao + 1;
  const publicado: ContratoConfig = {
    versao,
    versaoExigida: exigirNovoAceite ? versao : atual.versaoExigida,
    contratada: {
      nome: novo.contratada.nome.trim(),
      cpf: novo.contratada.cpf.trim(),
      endereco: novo.contratada.endereco.trim(),
    },
    foro: novo.foro.trim(),
    textos: novo.textos,
    atualizadoEm: Date.now(),
    atualizadoPor: uid,
  };
  await firestore.collection('config').doc('contrato').set(publicado);
  return publicado;
};
